// Persistent Market structure navigation: Market → Zone → Use Case.
// Authoritative counts from the active package (zone.counts.patternCount,
// useCase.counts.patternCount). No editorial/legacy counts.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { zoneColor } from "./LeadersChart";

export const MarketTree = ({ market, selectedZoneKey, selectedUseCaseKey, currentPattern }) => {
    const zones = market?.marketZones || [];
    const useCases = market?.useCases || [];
    const effUseCaseKey = selectedUseCaseKey || currentPattern?.useCaseKey || null;
    const parentZoneOf = useMemo(() => {
        const m = {};
        for (const u of useCases) for (const zk of u.marketZoneKeys || []) (m[zk] = m[zk] || []).push(u);
        return m;
    }, [useCases]);
    const selectedZoneFromUc = useMemo(() => {
        if (!effUseCaseKey) return null;
        const u = useCases.find((x) => x.scopeKey === effUseCaseKey);
        return u?.marketZoneKeys?.[0] || null;
    }, [useCases, effUseCaseKey]);
    const [open, setOpen] = useState(() => new Set([selectedZoneKey || selectedZoneFromUc].filter(Boolean)));
    const total = zones.reduce((s, z) => s + (z.counts?.patternCount || 0), 0);
    const toggle = (k) => setOpen((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

    return (
        <nav className="w-60 flex-shrink-0 hidden lg:block" data-testid="wp13-market-tree" aria-label="Market structure">
            <Link to="/market-v2/market"
                  data-testid="wp13-tree-market"
                  className={`flex items-baseline justify-between px-2.5 py-1.5 rounded text-[11.5px] font-semibold hover:bg-[hsl(var(--surface-sunken))] ${!selectedZoneKey && !selectedUseCaseKey ? "bg-[hsl(var(--accent-soft))]/50" : ""}`}>
                <span>Market</span>
                <span className="num text-[9px] text-[hsl(var(--mute))]">{total} patterns</span>
            </Link>
            <div className="mt-1 space-y-0.5">
                {zones.map((z) => {
                    const isOpen = open.has(z.scopeKey) || selectedZoneKey === z.scopeKey || selectedZoneFromUc === z.scopeKey;
                    const color = zoneColor(z.presentationColorToken);
                    const zSel = selectedZoneKey === z.scopeKey;
                    return (
                        <div key={z.scopeKey}>
                            <div className={`flex items-center gap-1 rounded pr-2 ${zSel ? "bg-[hsl(var(--accent-soft))]/50" : "hover:bg-[hsl(var(--surface-sunken))]"}`}>
                                <button type="button" onClick={() => toggle(z.scopeKey)} className="p-1 text-[hsl(var(--mute))]"
                                        data-testid={`wp13-tree-toggle-${z.scopeKey}`} aria-label={`Toggle ${z.label}`}>
                                    {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                </button>
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
                                <Link to={`/market-v2/market?zone=${encodeURIComponent(z.scopeKey)}`}
                                      data-testid={`wp13-tree-zone-${z.scopeKey}`}
                                      className={`flex-1 min-w-0 py-1 text-[10.5px] leading-tight truncate ${zSel ? "font-semibold" : ""}`}
                                      title={z.label}>{z.label}</Link>
                                <span className="num text-[9px] text-[hsl(var(--mute))] flex-shrink-0">{z.counts?.patternCount ?? "—"}</span>
                            </div>
                            {isOpen && (
                                <div className="ml-6 border-l border-[hsl(var(--line))] pl-2 space-y-0.5 py-0.5">
                                    {(parentZoneOf[z.scopeKey] || []).map((u) => (
                                        <React.Fragment key={u.scopeKey}>
                                            <Link to={`/market-v2/market/use-cases/${encodeURIComponent(u.scopeKey)}`}
                                                  data-testid={`wp13-tree-usecase-${u.scopeKey}`}
                                                  className={`flex items-baseline justify-between gap-2 px-1.5 py-0.5 rounded text-[10px] leading-tight hover:bg-[hsl(var(--surface-sunken))] ${
                                                      selectedUseCaseKey === u.scopeKey ? "bg-[hsl(var(--accent-soft))]/50 font-semibold" : "text-[hsl(var(--ink-soft))]"}`}>
                                                  <span className="truncate">{u.label}</span>
                                                  <span className="num text-[9px] text-[hsl(var(--mute))] flex-shrink-0">{u.counts?.patternCount ?? ""}</span>
                                            </Link>
                                            {currentPattern && currentPattern.useCaseKey === u.scopeKey && (
                                                <div className="ml-2.5 border-l border-[hsl(var(--line))] pl-2 py-0.5" data-testid="wp13-tree-current-pattern">
                                                    <span className="block px-1.5 py-0.5 rounded bg-[hsl(var(--accent-soft))]/60 text-[9.5px] font-semibold leading-tight truncate"
                                                          title={currentPattern.title}>{currentPattern.title}</span>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </nav>
    );
};

export const MarketLayout = ({ market, selectedZoneKey, selectedUseCaseKey, currentPattern, children }) => (
    <div className="flex gap-8 items-start">
        <MarketTree market={market} selectedZoneKey={selectedZoneKey} selectedUseCaseKey={selectedUseCaseKey} currentPattern={currentPattern} />
        <div className="flex-1 min-w-0">{children}</div>
    </div>
);
